import React, { useState, useEffect } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import financialConfigService, { EXPENSE_CATEGORIES, ALLOCATION_METHODS } from '../../services/financialConfigService';
import { getQuickBooksEntities } from '../../services/quickbooksService';
import brandService from '../../services/brandService';

export default function CostAllocationManager() {
  const { confirmDialogProps, confirm } = useConfirmDialog();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [qbEntities, setQbEntities] = useState([]);
  const [brands, setBrands] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [editingAllocation, setEditingAllocation] = useState(null);
  const [formData, setFormData] = useState({
    qb_connector_id: '',
    brand_id: '',
    effective_from: '',
    effective_to: '',
    allocation_percentage: '',
    allocation_method: 'manual_percentage',
    expense_category: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load cost allocations
      try {
        const allocationsRes = await financialConfigService.getCostAllocations();
        if (allocationsRes?.success && allocationsRes?.data) {
          setAllocations(allocationsRes.data);
        }
      } catch (err) {
        console.error('Error loading cost allocations:', err);
        setAllocations([]);
      }

      // Load QB entities
      try {
        const entitiesRes = await getQuickBooksEntities();
        if (entitiesRes?.success && entitiesRes?.data) {
          setQbEntities(entitiesRes.data);
        }
      } catch (err) {
        console.error('Error loading QB entities:', err);
        setQbEntities([]);
      }

      // Load brands
      try {
        const brandsRes = await brandService.getBrands();
        if (Array.isArray(brandsRes)) {
          setBrands(brandsRes);
        }
      } catch (err) {
        console.error('Error loading brands:', err);
        setBrands([]);
      }

    } catch (err) {
      console.error('Error loading cost allocation data:', err);
      setError('Failed to load cost allocation overrides. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (allocation = null) => {
    if (allocation) {
      setEditingAllocation(allocation);
      setFormData({
        qb_connector_id: allocation.qb_connector_id,
        brand_id: allocation.brand_id,
        effective_from: allocation.effective_from ? allocation.effective_from.split('T')[0] : '',
        effective_to: allocation.effective_to ? allocation.effective_to.split('T')[0] : '',
        allocation_percentage: allocation.allocation_percentage || '',
        allocation_method: allocation.allocation_method || 'manual_percentage',
        expense_category: allocation.expense_category || '',
        notes: allocation.notes || ''
      });
    } else {
      setEditingAllocation(null);
      setFormData({
        qb_connector_id: '',
        brand_id: '',
        effective_from: new Date().toISOString().split('T')[0],
        effective_to: '',
        allocation_percentage: '',
        allocation_method: 'manual_percentage',
        expense_category: '',
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAllocation(null);
  };

  const handlePreview = async () => {
    try {
      if (!formData.qb_connector_id || !formData.brand_id) {
        alert('Please select a QB Company and Brand');
        return;
      }

      const response = await financialConfigService.previewAllocation(formData);
      if (response?.success && response?.data) {
        setPreviewData(response.data);
        setPreviewDialogOpen(true);
      }
    } catch (err) {
      console.error('Error previewing allocation:', err);
      alert('Failed to preview allocation');
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.qb_connector_id || !formData.brand_id) {
        alert('Please select a QB Company and Brand');
        return;
      }

      if (!formData.effective_from) {
        alert('Please select an effective from date');
        return;
      }

      if (formData.allocation_method === 'manual_percentage' && !formData.allocation_percentage) {
        alert('Please enter an allocation percentage');
        return;
      }

      if (editingAllocation) {
        await financialConfigService.updateCostAllocation(editingAllocation.id, formData);
      } else {
        await financialConfigService.createCostAllocation(formData);
      }

      loadData();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving cost allocation:', err);
      alert('Failed to save cost allocation override');
    }
  };

  const handleDelete = async (id) => {
    if (!await confirm({ title: 'Delete Allocation', message: 'Are you sure you want to delete this cost allocation override?', confirmText: 'Delete', severity: 'danger' })) {
      return;
    }

    try {
      await financialConfigService.deleteCostAllocation(id);
      loadData();
    } catch (err) {
      console.error('Error deleting cost allocation:', err);
      alert('Failed to delete cost allocation override');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const getQBEntityName = (connectorId) => {
    const entity = qbEntities.find(e => e.connector_id === connectorId);
    return entity ? entity.company_name : 'Unknown';
  };

  const getBrandName = (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    return brand ? brand.name : `Brand ${brandId}`;
  };

  const getCategoryLabel = (value) => {
    if (!value) return 'All Categories';
    const category = EXPENSE_CATEGORIES.find(c => c.value === value);
    return category ? category.label : value;
  };

  const getAllocationMethodLabel = (value) => {
    const method = ALLOCATION_METHODS.find(m => m.value === value);
    return method ? method.label : value;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Ongoing';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={600}>
            Cost Allocation Overrides
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Override default allocation methods with custom percentages for specific brands and time periods
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Override
        </Button>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={600} gutterBottom>
          How Cost Allocation Overrides Work:
        </Typography>
        <Typography variant="body2">
          • <strong>Default Behavior</strong>: Costs are allocated to brands using revenue-based percentages
          <br />
          • <strong>Manual Override</strong>: Set a fixed percentage for a specific brand and QB company
          <br />
          • <strong>Time-Limited</strong>: Overrides can have start and end dates (or ongoing)
          <br />
          • <strong>Category-Specific</strong>: Apply overrides to specific expense categories or all categories
          <br />
          • <strong>Example</strong>: Allocate 25% of rent to Brand A for Q1 2025, then revert to revenue-based
        </Typography>
      </Alert>

      {/* Allocations Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>QB Company</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Allocation %</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Effective Period</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allocations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No cost allocation overrides configured. Click "Add Override" to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  allocations.map((allocation) => (
                    <TableRow key={allocation.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {getQBEntityName(allocation.qb_connector_id)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getBrandName(allocation.brand_id)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {allocation.allocation_percentage !== null ? (
                          <Chip
                            label={`${allocation.allocation_percentage}%`}
                            size="small"
                            color="primary"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">Default</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getAllocationMethodLabel(allocation.allocation_method)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getCategoryLabel(allocation.expense_category)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(allocation.effective_from)} - {formatDate(allocation.effective_to)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(allocation)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(allocation.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAllocation ? 'Edit Cost Allocation Override' : 'Add Cost Allocation Override'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>QuickBooks Company *</InputLabel>
              <Select
                value={formData.qb_connector_id}
                label="QuickBooks Company *"
                onChange={(e) => setFormData({ ...formData, qb_connector_id: e.target.value })}
              >
                {qbEntities.map((entity) => (
                  <MenuItem key={entity.connector_id} value={entity.connector_id}>
                    {entity.company_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Brand *</InputLabel>
              <Select
                value={formData.brand_id}
                label="Brand *"
                onChange={(e) => setFormData({ ...formData, brand_id: e.target.value })}
              >
                {brands.map((brand) => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Allocation Method *</InputLabel>
              <Select
                value={formData.allocation_method}
                label="Allocation Method *"
                onChange={(e) => setFormData({ ...formData, allocation_method: e.target.value })}
              >
                {ALLOCATION_METHODS.map((method) => (
                  <MenuItem key={method.value} value={method.value}>
                    {method.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.allocation_method === 'manual_percentage' && (
              <TextField
                fullWidth
                type="number"
                label="Allocation Percentage *"
                value={formData.allocation_percentage}
                onChange={(e) => setFormData({ ...formData, allocation_percentage: e.target.value })}
                helperText="Percentage of costs to allocate to this brand (0-100)"
                inputProps={{ min: 0, max: 100, step: 0.01 }}
              />
            )}

            <FormControl fullWidth>
              <InputLabel>Expense Category</InputLabel>
              <Select
                value={formData.expense_category}
                label="Expense Category"
                onChange={(e) => setFormData({ ...formData, expense_category: e.target.value })}
              >
                <MenuItem value="">All Categories</MenuItem>
                {EXPENSE_CATEGORIES.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="date"
              label="Effective From *"
              value={formData.effective_from}
              onChange={(e) => setFormData({ ...formData, effective_from: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="When this override takes effect"
            />

            <TextField
              fullWidth
              type="date"
              label="Effective To"
              value={formData.effective_to}
              onChange={(e) => setFormData({ ...formData, effective_to: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="When this override ends (leave empty for ongoing)"
            />

            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={3}
              placeholder="Optional notes about this override"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handlePreview}
            startIcon={<VisibilityIcon />}
            disabled={!formData.qb_connector_id || !formData.brand_id}
          >
            Preview
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.qb_connector_id || !formData.brand_id || !formData.effective_from}
          >
            {editingAllocation ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Allocation Preview</DialogTitle>
        <DialogContent>
          {previewData && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>QB Company:</strong> {getQBEntityName(formData.qb_connector_id)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Brand:</strong> {getBrandName(formData.brand_id)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Method:</strong> {getAllocationMethodLabel(formData.allocation_method)}
              </Typography>
              {formData.allocation_percentage && (
                <Typography variant="body2" gutterBottom>
                  <strong>Percentage:</strong> {formData.allocation_percentage}%
                </Typography>
              )}
              <Typography variant="body2" gutterBottom>
                <strong>Category:</strong> {getCategoryLabel(formData.expense_category)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Period:</strong> {formatDate(formData.effective_from)} - {formatDate(formData.effective_to)}
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                This override will be applied to all matching expense transactions during the specified period.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
}
